/**
 * DOMPurify allowlist config cho raw VBPL HTML render trong iframe srcdoc.
 *
 * Spec: specs/M2-EXECUTION-PLAN-v2.md §4 P-A5 (RawHtmlViewer).
 * Source HTML: raw R2 stream qua /api/v1/doc/:id/raw-html (vbpl.vn original).
 *
 * Allow list scope:
 *   - Block-level: p, div, section, article, h1-h6, blockquote, pre, hr, br
 *   - Inline: span, strong, em, b, i, u, sub, sup, mark, small
 *   - List: ul, ol, li, dl, dt, dd
 *   - Table: table, thead, tbody, tfoot, tr, td, th, caption
 *   - Anchor: a (href same-doc only, target removed)
 *   - Image: img (data-src removed to avoid CORS/PII leak, original src kept)
 *   - Inline id attr (cho article anchor) + class (preserve original layout)
 *
 * Block list (remove entirely):
 *   - script, style, link, meta, iframe, object, embed, form, input
 *   - on* event handler attr
 *   - javascript: / data: URL trong href
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  // Block
  'p', 'div', 'section', 'article', 'header', 'footer', 'main', 'nav', 'aside',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'pre', 'hr', 'br',
  // Inline
  'span', 'strong', 'em', 'b', 'i', 'u', 'sub', 'sup', 'mark', 'small', 'code',
  // List
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  // Table
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'colgroup', 'col',
  // Anchor + image
  'a', 'img',
  // Figure
  'figure', 'figcaption',
];

const ALLOWED_ATTR = [
  'id', 'class', 'style',
  'href', 'title', 'lang', 'dir',
  'colspan', 'rowspan', 'scope', 'align',
  'src', 'alt', 'width', 'height',
  'data-anchor', 'data-dieu',
];

/**
 * Sanitize raw VBPL HTML body. Returns string safe for iframe srcdoc.
 *
 * Pipeline:
 *   1. DOMPurify.sanitize with our allowlist
 *   2. Hook: rewrite anchor href to # (block external nav inside iframe)
 *   3. Hook: strip target attr (no _blank popup)
 *   4. Hook: strip javascript:/data: scheme URL
 */
export function sanitizeRawHtml(rawHtml: string): string {
  if (typeof window === 'undefined') {
    // SSR / SSG build: skip sanitization (iframe render happens client-side only).
    return '';
  }

  // Hook: strip dangerous attribute on every element.
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (!data) return;
    const { attrName, attrValue } = data;
    if (typeof attrName !== 'string') return;
    // Drop all event handler attr.
    if (attrName.startsWith('on')) {
      data.keepAttr = false;
      return;
    }
    // Drop unsafe URL scheme.
    if (
      (attrName === 'href' || attrName === 'src') &&
      typeof attrValue === 'string' &&
      /^\s*(javascript|data|vbscript|file):/i.test(attrValue)
    ) {
      data.keepAttr = false;
      return;
    }
    // Drop target attr (no popup escape from iframe).
    if (attrName === 'target') {
      data.keepAttr = false;
      return;
    }
  });

  const clean = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ['script', 'style', 'link', 'meta', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
    KEEP_CONTENT: true,
  });

  // Remove hook once done to avoid global side effect.
  DOMPurify.removeHook('uponSanitizeAttribute');

  return typeof clean === 'string' ? clean : String(clean ?? '');
}

/**
 * Build iframe srcdoc payload: wrap sanitized body voi inline style normalize
 * Times New Roman -> Inter (or var(--font-sans)), readable line-height + padding.
 *
 * iframe se chay sandbox="allow-same-origin" only (no allow-scripts).
 */
export function buildIframeSrcDoc(sanitizedBody: string): string {
  const css = `
    *, *::before, *::after { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #1a1a1a;
      font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 16px;
      line-height: 1.7;
    }
    body { padding: 24px 28px 48px; }
    h1, h2, h3, h4, h5, h6 {
      font-family: 'Source Serif Pro', Georgia, 'Times New Roman', serif;
      color: #0c1f3f;
      margin: 1.4em 0 0.4em;
    }
    h1 { font-size: 22px; }
    h2 { font-size: 19px; }
    h3 { font-size: 17px; }
    h4 { font-size: 16px; }
    p { margin: 0.6em 0; }
    a { color: #2b7cb3; text-decoration: underline; }
    a:hover { text-decoration: none; }
    table { border-collapse: collapse; width: 100%; margin: 12px 0; }
    th, td { border: 1px solid #d4d4d4; padding: 6px 10px; vertical-align: top; }
    th { background: #f4f4f4; }
    img { max-width: 100%; height: auto; }
    blockquote { margin: 1em 0; padding: 0.4em 1em; border-left: 4px solid #d4d4d4; color: #4a4a4a; }
    .dieu-anchor { scroll-margin-top: 16px; }
    @media (max-width: 480px) {
      body { padding: 16px 14px 32px; font-size: 15px; }
      table { font-size: 13px; }
      th, td { padding: 4px 6px; }
    }
    @media (prefers-color-scheme: dark) {
      html, body { background: #0e1422; color: #e6ecf7; }
      h1, h2, h3, h4, h5, h6 { color: #f1f5fb; }
      a { color: #7ab8e8; }
      th, td { border-color: #2a3550; }
      th { background: #1a2440; }
      blockquote { border-color: #2a3550; color: #cbd5e1; }
    }
  `;
  return `<!doctype html>
<html lang="vi">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>${css}</style>
</head>
<body>${sanitizedBody}</body>
</html>`;
}
